var __rest = this && this.__rest || function (s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Children, forwardRef, Fragment, useContext, useEffect, useState } from 'react';
import { Animated, FlatList, ScrollView, View } from 'react-native';
import { StateConfigContext } from '../../providers';
import { checkIsPlatform } from '../../utils';
import { getScrollableProps, getScrollableStyles } from './ScrollableStyles';
var FlatListComponent = FlatList;
if (!checkIsPlatform('web')) {
  var _require = require('react-native-gesture-handler'),
    _FlatList = _require.FlatList;
  FlatListComponent = _FlatList;
}
export var Scrollable = forwardRef((props, ref) => {
  var stateContext = useContext(StateConfigContext);
  var scrollableProps = getScrollableProps(props, stateContext);
  var scrollableStyles = getScrollableStyles(scrollableProps);
  var variant = scrollableProps.variant,
    horizontalScroll = scrollableProps.horizontalScroll,
    scrollIndicatorSize = scrollableProps.scrollIndicatorSize,
    onScroll = scrollableProps.onScroll,
    style = scrollableProps.style,
    children = scrollableProps.children,
    _scrollableProps$show = scrollableProps.showScrollbar,
    showScrollbar = _scrollableProps$show === void 0 ? true : _scrollableProps$show,
    restProps = __rest(scrollableProps, ["variant", "horizontalScroll", "scrollIndicatorSize", "onScroll", "style", "children", "showScrollbar"]);
  var _useState = useState(1),
    fullSizeContentHeight = _useState[0],
    setFullSizeContentHeight = _useState[1];
  var _useState2 = useState(1),
    visibleScrollPartHeight = _useState2[0],
    setVisibleScrollPartHeight = _useState2[1];
  var _useState3 = useState(6),
    indicatorFlexibleHeight = _useState3[0],
    setIndicatorFlexibleHeight = _useState3[1];
  var _useState4 = useState(1),
    scrollIndicatorContainerHeight = _useState4[0],
    setScrollIndicatorContainerHeight = _useState4[1];
  var _useState5 = useState(0),
    indicatorOffset = _useState5[0],
    setIndicatorOffset = _useState5[1];
  var _useState6 = useState(6),
    indicatorFlexibleWidth = _useState6[0],
    setIndicatorFlexibleWidth = _useState6[1];
  var _useState7 = useState(1),
    scrollIndicatorContainerWidth = _useState7[0],
    setScrollIndicatorContainerWidth = _useState7[1];
  var _useState8 = useState(1),
    visibleScrollPartWidth = _useState8[0],
    setVisibleScrollPartWidth = _useState8[1];
  var _useState9 = useState(1),
    fullSizeContentWidth = _useState9[0],
    setFullSizeContentWidth = _useState9[1];
  var handleScroll = event => {
    var _a;
    onScroll === null || onScroll === void 0 ? void 0 : onScroll(event);
    if (showScrollbar) {
      var contentOffset = (_a = event === null || event === void 0 ? void 0 : event.nativeEvent) === null || _a === void 0 ? void 0 : _a.contentOffset;
      var movePercent = horizontalScroll ? (contentOffset === null || contentOffset === void 0 ? void 0 : contentOffset.x) / ((fullSizeContentWidth - visibleScrollPartWidth) / 100) : (contentOffset === null || contentOffset === void 0 ? void 0 : contentOffset.y) / ((fullSizeContentHeight - visibleScrollPartHeight) / 100);
      var position = (horizontalScroll ? visibleScrollPartWidth - indicatorFlexibleWidth - (visibleScrollPartWidth - scrollIndicatorContainerWidth) : visibleScrollPartHeight - indicatorFlexibleHeight - (visibleScrollPartHeight - scrollIndicatorContainerHeight)) / 100 * movePercent;
      if (position > 0) setIndicatorOffset(position);
    }
  };
  useEffect(() => {
    !horizontalScroll ? setIndicatorFlexibleHeight(visibleScrollPartHeight * (visibleScrollPartHeight / fullSizeContentHeight)) : setIndicatorFlexibleWidth(visibleScrollPartWidth * (visibleScrollPartWidth / fullSizeContentWidth));
  }, [horizontalScroll, visibleScrollPartHeight, fullSizeContentHeight, fullSizeContentWidth, visibleScrollPartWidth]);
  var Component = variant == 'flatList' ? FlatListComponent : ScrollView;
  return _jsx(View, Object.assign({
    style: [scrollableStyles.container, style]
  }, {
    children: _jsxs(View, Object.assign({
      style: scrollableStyles.content
    }, {
      children: [_jsx(Component, Object.assign({}, showScrollbar && {
        onContentSizeChange: (width, height) => {
          if (horizontalScroll) {
            setFullSizeContentWidth(width);
          } else {
            setFullSizeContentHeight(height);
          }
        },
        onLayout: e => {
          var _a, _b, _c, _d;
          if (horizontalScroll) {
            setVisibleScrollPartWidth((_b = (_a = e === null || e === void 0 ? void 0 : e.nativeEvent) === null || _a === void 0 ? void 0 : _a.layout) === null || _b === void 0 ? void 0 : _b.width);
          } else {
            setVisibleScrollPartHeight((_d = (_c = e === null || e === void 0 ? void 0 : e.nativeEvent) === null || _c === void 0 ? void 0 : _c.layout) === null || _d === void 0 ? void 0 : _d.height);
          }
        }
      }, {
        onScroll: handleScroll,
        horizontal: horizontalScroll,
        scrollEventThrottle: 16,
        showsVerticalScrollIndicator: false
      }, restProps, variant == 'flatList' && {
        data: new Array(Children.count(children)).fill(''),
        keyExtractor: (_, index) => "single_child_" + index,
        renderItem: _ref => {
          var index = _ref.index;
          return _jsx(Fragment, {
            children: Children.map(children, (child, i) => i === index ? child : null)
          }, "single_child_" + index);
        }
      }, {
        ref: ref
      }, {
        children: children
      })), showScrollbar && _jsx(View, Object.assign({
        style: [scrollableStyles.indicatorContainer],
        onLayout: e => {
          if (horizontalScroll) {
            setScrollIndicatorContainerWidth(e.nativeEvent.layout.width);
          } else {
            setScrollIndicatorContainerHeight(e.nativeEvent.layout.height);
          }
        }
      }, {
        children: _jsx(Animated.View, {
          style: [scrollableStyles.indicator, {
            top: horizontalScroll ? 0 : indicatorOffset,
            left: !horizontalScroll ? 0 : indicatorOffset,
            height: horizontalScroll ? scrollIndicatorSize : indicatorFlexibleHeight,
            width: horizontalScroll ? indicatorFlexibleWidth : scrollIndicatorSize
          }]
        })
      }))]
    }))
  }));
});